import { Controller } from "@hotwired/stimulus"

/**
 * Preview Refresh Controller
 * Listens for tab changes and refreshes preview content when switching to preview tab
 */
export default class extends Controller {
  static values = {
    taskId: Number,
    reportId: Number
  }

  connect() {
    // Listen for tab change events from tabs controller
    this.boundHandleTabChange = this.handleTabChange.bind(this)
    this.element.addEventListener('tabs:change', this.boundHandleTabChange)

    // Flag to track if we're refreshing
    this.isRefreshing = false
  }

  disconnect() {
    this.element.removeEventListener('tabs:change', this.boundHandleTabChange)
  }

  /**
   * Handle tab change event
   * @param {CustomEvent} event - Tab change event
   */
  handleTabChange(event) {
    const { index, panel } = event.detail

    // Check if switching to preview panel (index 4 based on tab order)
    if (panel && panel.id === 'preview-panel') {
      // Refresh everything to ensure images, captions, and all fields are current
      this.refreshFullPreview()
      this.refreshPreview()
      this.refreshOptionalHeadings()
      this.refreshTitle()
    }
  }

  /**
   * Refresh the ENTIRE preview panel by fetching latest HTML from server
   * This ensures images, captions, and all fields are up-to-date
   */
  async refreshFullPreview() {
    if (this.isRefreshing) {
      return
    }

    this.isRefreshing = true

    try {
      // Show loading indicator
      this.showLoadingIndicator()

      // Fetch the preview tab content from server
      const response = await fetch(this.previewUrl(), {
        headers: {
          'Accept': 'text/html',
          'X-CSRF-Token': this.csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const html = await response.text()

      // Find the preview panel
      const previewPanel = document.querySelector('#preview-panel')
      if (!previewPanel) {
        console.error('[preview-refresh] Preview panel not found')
        return
      }

      // Replace the entire panel content with fresh HTML
      previewPanel.innerHTML = html

      // Force reload all images to fix broken image issue
      this.reloadImages(previewPanel)

      // Hide loading indicator
      this.hideLoadingIndicator()

    } catch (error) {
      console.error('[preview-refresh] Failed to refresh preview:', error)
      this.hideLoadingIndicator()
      this.showErrorMessage()
    } finally {
      this.isRefreshing = false
    }
  }

  /**
   * Refresh preview content from server
   */
  async refreshPreview() {
    if (this.isRefreshing) {
      return
    }

    this.isRefreshing = true

    try {
      // Show loading indicator
      this.showLoadingIndicator()

      // Fetch latest pages
      const response = await fetch(this.pagesUrl(), {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data = await response.json()

      // Update the additional pages section in preview
      this.updateAdditionalPagesPreview(data.pages)

      // Hide loading indicator
      this.hideLoadingIndicator()

    } catch (error) {
      console.error('[preview-refresh] Failed to refresh pages:', error)
      this.hideLoadingIndicator()
      this.showErrorMessage()
    } finally {
      this.isRefreshing = false
    }
  }

  /**
   * Update additional pages section in preview
   * @param {Array} pages - Array of page objects
   */
  updateAdditionalPagesPreview(pages) {
    const previewContainer = document.querySelector('.preview-container')
    if (!previewContainer) {
      return
    }

    // Find or create additional pages section
    let additionalPagesSection = previewContainer.querySelector('.additional-pages-preview-section')

    if (pages.length === 0) {
      // Remove section if no pages
      if (additionalPagesSection) {
        additionalPagesSection.remove()
      }
      return
    }

    if (!additionalPagesSection) {
      // Create the section
      additionalPagesSection = document.createElement('div')
      additionalPagesSection.className = 'additional-pages-preview-section mt-4'
      additionalPagesSection.innerHTML = '<hr class="my-4">'

      // Insert BEFORE the Action Buttons row to keep buttons at the bottom
      const actionButtonsRow = previewContainer.querySelector('.row.mt-4.mb-5')
      if (actionButtonsRow) {
        previewContainer.insertBefore(additionalPagesSection, actionButtonsRow)
      } else {
        previewContainer.appendChild(additionalPagesSection)
      }
    } else {
      // Clear existing pages (keep the hr)
      const existingCards = additionalPagesSection.querySelectorAll('.card[data-preview-page-id]')
      existingCards.forEach(card => card.remove())
    }

    // Add each page as a card
    pages.forEach((page, index) => {
      const card = this.createPageCard(page, index)
      additionalPagesSection.appendChild(card)
    })
  }

  /**
   * Create a page preview card
   * @param {Object} page - Page data
   * @param {number} index - Page index
   * @returns {Element} Card element
   */
  createPageCard(page, index) {
    const card = document.createElement('div')
    card.className = 'card mb-4 shadow-sm'
    card.setAttribute('data-preview-page-id', page.id)

    const title = page.title || `Additional Page ${index + 1}`
    const content = page.content || '<p class="text-muted">No content yet</p>'

    // Create card structure
    card.innerHTML = `
      <div class="card-header bg-warning text-dark">
        <h5 class="mb-0">
          <i class="fas fa-file-alt mr-2"></i>
          ${this.escapeHtml(title)}
        </h5>
      </div>
      <div class="card-body">
        <div class="additional-page-content" style="min-height: 100px; padding: 15px; background-color: #fafafa; border-radius: 5px; word-wrap: break-word;">
          ${content}
        </div>
      </div>
    `

    return card
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * Format text with line breaks (similar to Rails simple_format)
   * @param {string} text - Text to format
   * @returns {string} Formatted HTML text
   */
  formatText(text) {
    if (!text) return ''

    // Escape HTML first
    const escaped = this.escapeHtml(text)

    // Convert newlines to <br> tags and wrap in <p>
    const paragraphs = escaped.split('\n\n')
    return '<p>' + paragraphs.map(p => p.replace(/\n/g, '<br>')).join('</p><p>') + '</p>'
  }

  /**
   * Get preview panel URL (fetches latest preview HTML)
   * @returns {string} Preview URL
   */
  previewUrl() {
    const locale = this.getLocale()
    return `/${locale}/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/preview`
  }

  /**
   * Get pages API URL
   * @returns {string} Pages URL
   */
  pagesUrl() {
    const locale = this.getLocale()
    return `/${locale}/tasks/${this.taskIdValue}/reports/${this.reportIdValue}/pages`
  }

  /**
   * Get current locale from URL or default to 'en'
   * @returns {string} Locale code
   */
  getLocale() {
    const pathParts = window.location.pathname.split('/')
    // Check if first part is a locale (en or nn)
    if (pathParts[1] && (pathParts[1] === 'en' || pathParts[1] === 'nn')) {
      return pathParts[1]
    }
    return 'en' // Default locale
  }

  /**
   * Get CSRF token
   * @returns {string} CSRF token
   */
  get csrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }

  /**
   * Show loading indicator in preview panel
   */
  showLoadingIndicator() {
    const previewPanel = document.querySelector('#preview-panel')
    if (!previewPanel) return

    // Remove any existing loading indicator
    const existingIndicator = previewPanel.querySelector('.preview-loading-indicator')
    if (existingIndicator) {
      existingIndicator.remove()
    }

    // Create and show loading indicator
    const indicator = document.createElement('div')
    indicator.className = 'preview-loading-indicator'
    indicator.innerHTML = `
      <div class="text-center py-4">
        <i class="fa fa-spinner fa-spin fa-2x text-primary mb-2"></i>
        <p class="text-muted">Loading latest preview...</p>
      </div>
    `

    previewPanel.insertBefore(indicator, previewPanel.firstChild)
  }

  /**
   * Hide loading indicator
   */
  hideLoadingIndicator() {
    const indicator = document.querySelector('.preview-loading-indicator')
    if (indicator) {
      indicator.remove()
    }
  }

  /**
   * Show error message
   */
  showErrorMessage() {
    const previewPanel = document.querySelector('#preview-panel')
    if (!previewPanel) return

    const errorAlert = document.createElement('div')
    errorAlert.className = 'alert alert-warning alert-dismissible fade show'
    errorAlert.setAttribute('role', 'alert')
    errorAlert.innerHTML = `
      <button type="button" class="close" data-dismiss="alert" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
      <i class="fa fa-exclamation-triangle mr-2"></i>
      Failed to refresh preview. Please try switching tabs again.
    `

    previewPanel.insertBefore(errorAlert, previewPanel.firstChild)

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      if (errorAlert.parentNode) {
        errorAlert.remove()
      }
    }, 5000)
  }

  /**
   * Setup error handlers and retry logic for images in the container
   * If an image fails to load (e.g., DICOM still converting), retry after a delay
   * @param {Element} container - Container with images
   */
  reloadImages(container) {
    const images = container.querySelectorAll('img')

    images.forEach((img, index) => {
      if (!img.src || img.src === '') {
        return
      }

      // Track retry attempts - max 3 retries with 2 second delay
      const maxRetries = 3
      const retryDelay = 2000
      let retryCount = img.dataset.retryCount ? parseInt(img.dataset.retryCount) : 0

      // Add load handler for successful loads
      img.onload = () => {
        img.style.display = '' // Ensure visible
        img.dataset.retryCount = '0' // Reset retry count on success
      }

      // Add error handler with retry logic for failed loads
      img.onerror = () => {
        if (retryCount < maxRetries) {
          // Retry after delay - image might still be converting from DICOM
          retryCount++
          img.dataset.retryCount = retryCount.toString()

          setTimeout(() => {
            // Add cache buster to force fresh request
            const url = new URL(img.src, window.location.origin)
            url.searchParams.set('retry', Date.now().toString())
            img.src = url.toString()
          }, retryDelay)
        }
      }
    })
  }

  /**
   * Refresh optional headings in preview from form values
   * Reads form fields and updates the preview DOM without server call
   */
  refreshOptionalHeadings() {
    // Find the optional headings section in preview
    const previewPanel = document.querySelector('#preview-panel')
    if (!previewPanel) {
      return
    }

    // Read form values for all 4 optional headings
    const headings = []
    const wordIndices = ['one', 'two', 'three', 'four']

    wordIndices.forEach((wordIndex, i) => {
      const headingField = document.querySelector(`input[name="report[optional_heading_${wordIndex}]"]`)
      const textField = document.querySelector(`input[name="report[optional_text_${wordIndex}]"]`)

      const headingValue = headingField ? headingField.value.trim() : ''
      const textValue = textField ? textField.value.trim() : ''

      if (headingValue || textValue) {
        headings.push({
          index: i + 1,
          wordIndex: wordIndex,
          heading: headingValue,
          text: textValue
        })
      }
    })

    // Find or create the optional headings section in preview
    let optionalHeadingsSection = previewPanel.querySelector('.optional-headings-preview-section')

    // Find the reference point (insert after procedure metadata .bg-light section, before client info)
    const procedureMetadata = previewPanel.querySelector('.bg-light.p-3.rounded.mb-4')

    if (headings.length === 0) {
      // Remove section if no headings
      if (optionalHeadingsSection) {
        optionalHeadingsSection.remove()
      }
      return
    }

    if (!optionalHeadingsSection) {
      // Create the section
      optionalHeadingsSection = document.createElement('div')
      optionalHeadingsSection.className = 'optional-headings-preview-section card shadow-sm mb-4'

      // Insert after the procedure metadata section
      if (procedureMetadata && procedureMetadata.nextElementSibling) {
        procedureMetadata.parentNode.insertBefore(optionalHeadingsSection, procedureMetadata.nextElementSibling)
      } else if (procedureMetadata) {
        procedureMetadata.parentNode.insertBefore(optionalHeadingsSection, procedureMetadata.nextSibling)
      } else {
        // Fallback: append to preview panel
        const cardBody = previewPanel.querySelector('.card-body')
        if (cardBody) {
          cardBody.appendChild(optionalHeadingsSection)
        }
      }
    }

    // Build the HTML for optional headings
    let tableRows = ''
    headings.forEach(item => {
      // Format text with line breaks (similar to simple_format)
      const formattedText = this.formatText(item.text)

      tableRows += `
        <tr>
          <th class="bg-light" style="width: 25%;">${this.escapeHtml(item.heading)}</th>
          <td style="width: 75%;">${formattedText}</td>
        </tr>
      `
    })

    // Update the section content
    optionalHeadingsSection.innerHTML = `
      <div class="card-header bg-dark text-white shadow-sm">
        <h5 class="mb-0">
          <i class="fa fa-info-circle mr-2"></i>
          Additional Information
        </h5>
      </div>
      <div class="card-body p-0">
        <table class="table table-bordered mb-0">
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </div>
    `
  }

  /**
   * Refresh title in preview from form values
   * Updates the main title (inspection_of) and subtitle (test method name)
   */
  refreshTitle() {
    const previewPanel = document.querySelector('#preview-panel')
    if (!previewPanel) {
      return
    }

    // Read inspection_of field value
    const inspectionOfField = document.querySelector('input[name="report[inspection_of]"]')
    const inspectionOf = inspectionOfField ? inspectionOfField.value.trim() : ''

    // Find the title container in preview (inside .col-md-9 within the border-bottom row)
    const titleContainer = previewPanel.querySelector('.row.mb-4.pb-3.border-bottom .col-md-9')
    if (!titleContainer) {
      return
    }

    // Get the test method name (we'll keep it as-is since it's server-rendered)
    const testMethodElement = titleContainer.querySelector('p.text-muted')
    const testMethodName = testMethodElement ? testMethodElement.textContent.trim() : ''

    if (inspectionOf) {
      // Update with inspection_of as main title and test method as subtitle
      titleContainer.innerHTML = `
        <h2 class="mb-1 font-weight-bold">${this.escapeHtml(inspectionOf)}</h2>
        ${testMethodName ? `<p class="text-muted mb-0" style="font-size: 1.1rem;">${this.escapeHtml(testMethodName)}</p>` : ''}
      `
    } else {
      // Fallback: just show test method name as main title
      titleContainer.innerHTML = `
        <h2 class="mb-0 font-weight-bold">${testMethodName ? this.escapeHtml(testMethodName) : 'Test Method Report'}</h2>
      `
    }
  }
}
